'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Review {
  id: string;
  customerName: string;
  customerEmail: string;
  rating: number;
  title: string;
  content: string;
  service: string;
  destination: string;
  date: string;
  status: 'pending' | 'approved' | 'rejected';
  adminReply?: string;
  helpful: number;
  verified: boolean;
}

const MOCK_REVIEWS: Review[] = [
  {
    id: 'rev-001',
    customerName: 'Sarah Thompson',
    customerEmail: 'sarah.t@email.com',
    rating: 5,
    title: 'Absolutely Incredible Safari Experience!',
    content: 'The African Safari package exceeded all our expectations. The team at Dialforflight handled every detail perfectly from the visa assistance to the luxury tented camps. Our guide was knowledgeable and passionate. Highly recommend to anyone looking for a once-in-a-lifetime adventure.',
    service: 'African Safari Package',
    destination: 'Kenya & Tanzania',
    date: '2026-02-28',
    status: 'pending',
    helpful: 24,
    verified: true,
  },
  {
    id: 'rev-002',
    customerName: 'James Okafor',
    customerEmail: 'j.okafor@email.com',
    rating: 4,
    title: 'Great European Tour, Minor Delays',
    content: 'The Grand European Adventure was well-organised and the itinerary was packed with amazing experiences. The skip-the-line access at major attractions was a huge plus. Only minor issue was a 2-hour delay at Barcelona airport, but the team kept us informed throughout.',
    service: 'Grand European Adventure',
    destination: 'Paris, Rome & Barcelona',
    date: '2026-02-25',
    status: 'approved',
    adminReply: 'Thank you for your kind review, James! We apologise for the airport delay. We hope to welcome you back soon!',
    helpful: 18,
    verified: true,
  },
  {
    id: 'rev-003',
    customerName: 'Priya Sharma',
    customerEmail: 'priya.s@email.com',
    rating: 2,
    title: 'Disappointed with Hotel Quality',
    content: 'The flights were fine but the hotel in Bali was nothing like the photos shown on the website. The room was smaller and the pool was under maintenance. I expected better for the price paid. Customer service was slow to respond when I raised the issue.',
    service: 'Bali Paradise Escape',
    destination: 'Bali & Gili Islands',
    date: '2026-02-20',
    status: 'pending',
    helpful: 7,
    verified: true,
  },
  {
    id: 'rev-004',
    customerName: 'Michael Chen',
    customerEmail: 'm.chen@email.com',
    rating: 5,
    title: 'Japan Trip Was Flawless',
    content: 'From booking to return, everything was seamless. The PNR guarantee gave us peace of mind. The bullet train experience and tea ceremony were highlights. Our travel consultant was incredibly helpful and responsive throughout the planning process.',
    service: 'Japanese Cultural Immersion',
    destination: 'Tokyo to Kyoto',
    date: '2026-02-18',
    status: 'approved',
    helpful: 31,
    verified: true,
  },
  {
    id: 'rev-005',
    customerName: 'Anonymous User',
    customerEmail: 'anon@email.com',
    rating: 1,
    title: 'Very Disappointed',
    content: 'Service did not meet expectations. Would not recommend based on my experience.',
    service: 'Unknown',
    destination: 'Unknown',
    date: '2026-02-15',
    status: 'rejected',
    helpful: 0,
    verified: false,
  },
  {
    id: 'rev-006',
    customerName: 'Emma Williams',
    customerEmail: 'emma.w@email.com',
    rating: 4,
    title: 'Northern Lights Were Magical',
    content: 'The Scandinavian trip was breathtaking. Seeing the Northern Lights was a dream come true. The husky sledding was an unexpected highlight. Would have given 5 stars but the ice hotel was colder than expected.',
    service: 'Scandinavian Northern Lights Quest',
    destination: 'Norway',
    date: '2026-02-10',
    status: 'pending',
    helpful: 15,
    verified: true,
  },
];

type FilterStatus = 'all' | 'pending' | 'approved' | 'rejected';

const ReviewsManagement = () => {
  const [reviews, setReviews] = useState<Review[]>(MOCK_REVIEWS);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterRating, setFilterRating] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [expandedReview, setExpandedReview] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState('');

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3000);
  };

  const handleApprove = (id: string) => {
    setReviews(prev => prev.map(r => r.id === id ? { ...r, status: 'approved' as const } : r));
    showToast('Review approved and published');
  };

  const handleReject = (id: string) => {
    setReviews(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected' as const } : r));
    showToast('Review rejected and hidden from public');
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to permanently delete this review?')) {
      setReviews(prev => prev.filter(r => r.id !== id));
      showToast('Review permanently deleted');
    }
  };

  const handleReplySubmit = (id: string) => {
    if (!replyText.trim()) return;
    setReviews(prev => prev.map(r => r.id === id ? { ...r, adminReply: replyText } : r));
    setReplyingTo(null);
    setReplyText('');
    showToast('Reply posted successfully');
  };

  const handleDeleteReply = (id: string) => {
    setReviews(prev => prev.map(r => r.id === id ? { ...r, adminReply: undefined } : r));
    showToast('Reply deleted');
  };

  const filteredReviews = reviews.filter(r => {
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (filterRating > 0 && r.rating !== filterRating) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return r.customerName.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        r.destination.toLowerCase().includes(q) ||
        r.service.toLowerCase().includes(q);
    }
    return true;
  });

  const approvedReviews = reviews.filter(r => r.status === 'approved');
  const stats = {
    total: reviews.length,
    pending: reviews.filter(r => r.status === 'pending').length,
    approved: approvedReviews.length,
    rejected: reviews.filter(r => r.status === 'rejected').length,
    avgRating: approvedReviews.length > 0
      ? (approvedReviews.reduce((sum, r) => sum + r.rating, 0) / approvedReviews.length).toFixed(1)
      : '0.0',
  };

  const getStatusBadge = (status: Review['status']) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700 border border-green-200';
      case 'rejected': return 'bg-red-100 text-red-700 border border-red-200';
      case 'pending': return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
    }
  };

  const renderStars = (rating: number) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <Icon
          key={star}
          name="StarIcon"
          size={16}
          className={star <= rating ? 'text-accent' : 'text-gray-200'}
          variant={star <= rating ? 'solid' : 'outline'}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {toastMsg && (
        <div className="fixed top-6 right-6 z-50 bg-primary text-white px-5 py-3 rounded-xl shadow-lg font-semibold text-sm flex items-center gap-2">
          <Icon name="CheckCircleIcon" size={18} variant="solid" />
          {toastMsg}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Total Reviews', value: stats.total, icon: 'ChatBubbleLeftRightIcon', color: 'text-primary', bg: 'bg-primary/10' },
          { label: 'Pending', value: stats.pending, icon: 'ClockIcon', color: 'text-yellow-600', bg: 'bg-yellow-50' },
          { label: 'Approved', value: stats.approved, icon: 'CheckCircleIcon', color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Rejected', value: stats.rejected, icon: 'XCircleIcon', color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Avg Rating', value: stats.avgRating, icon: 'StarIcon', color: 'text-accent', bg: 'bg-accent/10' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl border border-border p-4 flex items-center gap-3">
            <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
              <Icon name={stat.icon as any} size={20} className={stat.color} />
            </div>
            <div>
              <p className="text-xl font-black text-foreground">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-border p-4 flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-48 relative">
          <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search reviews..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary"
          />
        </div>

        <div className="flex items-center gap-2">
          {(['all', 'pending', 'approved', 'rejected'] as FilterStatus[]).map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-3 py-2 rounded-lg text-sm font-bold capitalize transition-all ${
                filterStatus === status ? 'bg-primary text-white' : 'bg-surface text-muted-foreground hover:text-foreground'
              }`}
            >
              {status}
              {status !== 'all' && (
                <span className="ml-1.5 text-xs">
                  ({status === 'pending' ? stats.pending : status === 'approved' ? stats.approved : stats.rejected})
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rating:</span>
          <select
            value={filterRating}
            onChange={(e) => setFilterRating(Number(e.target.value))}
            className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary"
          >
            <option value={0}>All</option>
            {[5, 4, 3, 2, 1].map(r => (
              <option key={r} value={r}>{r} stars</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {filteredReviews.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-border">
            <Icon name="ChatBubbleLeftRightIcon" size={48} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-foreground font-semibold">No reviews found</p>
            <p className="text-muted-foreground text-sm">Try adjusting your filters</p>
          </div>
        ) : (
          filteredReviews.map((review) => (
            <div key={review.id} className="bg-white rounded-xl border border-border overflow-hidden">
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-black text-sm">{review.customerName.charAt(0)}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-bold text-foreground">{review.customerName}</span>
                        {review.verified && (
                          <span className="flex items-center gap-1 text-xs text-trust-green font-semibold">
                            <Icon name="ShieldCheckIcon" size={12} variant="solid" />
                            Verified
                          </span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${getStatusBadge(review.status)}`}>
                          {review.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mb-2">
                        <span>{review.customerEmail}</span>
                        <span>•</span>
                        <span>{new Date(review.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        <span>•</span>
                        <span>{review.service}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Icon name="MapPinIcon" size={10} />
                          {review.destination}
                        </span>
                      </div>
                      {renderStars(review.rating)}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {review.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(review.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 text-white text-xs font-bold rounded-lg hover:bg-green-600 transition-colors"
                        >
                          <Icon name="CheckIcon" size={14} />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(review.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white text-xs font-bold rounded-lg hover:bg-red-600 transition-colors"
                        >
                          <Icon name="XMarkIcon" size={14} />
                          Reject
                        </button>
                      </>
                    )}
                    {review.status === 'rejected' && (
                      <button
                        onClick={() => handleApprove(review.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 text-xs font-bold rounded-lg hover:bg-green-200 transition-colors"
                      >
                        <Icon name="ArrowPathIcon" size={14} />
                        Re-approve
                      </button>
                    )}
                    {review.status === 'approved' && (
                      <button
                        onClick={() => handleReject(review.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 text-yellow-700 text-xs font-bold rounded-lg hover:bg-yellow-200 transition-colors"
                      >
                        <Icon name="EyeSlashIcon" size={14} />
                        Unpublish
                      </button>
                    )}
                    <button
                      onClick={() => setReplyingTo(replyingTo === review.id ? null : review.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary text-xs font-bold rounded-lg hover:bg-primary/20 transition-colors"
                    >
                      <Icon name="ChatBubbleLeftIcon" size={14} />
                      Reply
                    </button>
                    <button
                      onClick={() => setExpandedReview(expandedReview === review.id ? null : review.id)}
                      className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Icon name={expandedReview === review.id ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(review.id)}
                      className="p-1.5 text-red-400 hover:text-red-600 transition-colors"
                      title="Delete permanently"
                    >
                      <Icon name="TrashIcon" size={18} />
                    </button>
                  </div>
                </div>

                <div className="mt-4 pl-14">
                  <h4 className="font-bold text-foreground mb-2">{review.title}</h4>
                  <p className={`text-sm text-foreground leading-relaxed ${expandedReview === review.id ? '' : 'line-clamp-3'}`}>
                    {review.content}
                  </p>
                  {review.content.length > 200 && (
                    <button
                      onClick={() => setExpandedReview(expandedReview === review.id ? null : review.id)}
                      className="text-xs text-primary font-semibold mt-1 hover:underline"
                    >
                      {expandedReview === review.id ? 'Show less' : 'Read more'}
                    </button>
                  )}
                  <div className="flex items-center gap-3 mt-3">
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Icon name="HandThumbUpIcon" size={14} />
                      {review.helpful} found helpful
                    </span>
                  </div>
                </div>
              </div>

              {review.adminReply && (
                <div className="mx-5 mb-4 bg-primary/5 border border-primary/20 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="BuildingOfficeIcon" size={14} className="text-white" />
                      </div>
                      <div>
                        <p className="text-xs font-black text-primary mb-1">Official Response from Dialforflight</p>
                        <p className="text-sm text-foreground leading-relaxed">{review.adminReply}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteReply(review.id)}
                      className="text-muted-foreground hover:text-red-500 transition-colors flex-shrink-0"
                      title="Delete reply"
                    >
                      <Icon name="TrashIcon" size={14} />
                    </button>
                  </div>
                </div>
              )}

              {replyingTo === review.id && (
                <div className="mx-5 mb-4 bg-surface rounded-xl p-4 border border-border">
                  <p className="text-sm font-bold text-foreground mb-3">Write Official Reply</p>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write a professional, helpful response to this customer review..."
                    rows={4}
                    className="w-full px-4 py-3 border border-border rounded-lg text-sm focus:outline-none focus:border-primary resize-none"
                  />
                  <div className="flex items-center gap-3 mt-3">
                    <button
                      onClick={() => handleReplySubmit(review.id)}
                      disabled={!replyText.trim()}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-bold rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
                    >
                      <Icon name="PaperAirplaneIcon" size={14} />
                      Post Reply
                    </button>
                    <button
                      onClick={() => { setReplyingTo(null); setReplyText(''); }}
                      className="px-4 py-2 text-sm font-bold text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="bg-white rounded-xl border border-border p-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing <span className="font-bold text-foreground">{filteredReviews.length}</span> of <span className="font-bold text-foreground">{reviews.length}</span> reviews
        </p>
        <div className="flex items-center gap-2">
          <Icon name="StarIcon" size={16} className="text-accent" variant="solid" />
          <span className="text-sm font-bold text-foreground">Average: {stats.avgRating}/5.0</span>
          <span className="text-xs text-muted-foreground">(approved reviews)</span>
        </div>
      </div>
    </div>
  );
};

export default ReviewsManagement;
